import { forwardRef, ReactNode, useEffect, useState } from 'react'
import * as React from 'react'
import { Slider } from 'antd'
import Cropper, { CropperProps } from 'react-easy-crop'
import externalService from 'api/externalService'
import getCroppedImg from 'utils/cropImage'
import { getCropperZoom, readImage, cropperImageClass } from 'utils/cropper'
import { FieldHookConfig } from 'formik/dist/Field'
import { useField } from 'formik'
import { Button } from 'components/Button/Button'
import { ReactComponent as CameraIcon } from 'assets/icons/cameraAdd.svg'
import { ReactComponent as CloseIcon } from 'assets/icons/close24.svg'
import { useObjectRef } from 'utils/useObjectRef'
import { useField as useInputField } from '@react-aria/label'
import { useButton } from '@react-aria/button'
import { AriaButtonProps } from '@react-types/button'
import { useHover } from '@react-aria/interactions'
import { useFocusRing } from '@react-aria/focus'
import { PressEvent } from '@react-types/shared/src/events'
import { Modal } from 'antd'
import { showToastMessage } from 'common/ToastMessage/ShowToastMessage'
import cx from 'classnames'

import { ReactComponent as RemoveIcon } from './close.svg'
import Styles from './ImageUploadFieldModal.module.scss'

const CROP_DIAMETER = 160
const MAX_ZOOM_MULTIPLIER = 2.5

const antdSliderHandleStyles = {
  background: 'linear-gradient(180deg, #B925FF 0%, #9F2BEB 100%)',
  width: '24px',
  height: '24px',
  'box-shadow': '-1px 3px 3px rgba(0, 0, 0, 0.25)',
  'margin-top': '-8px',
}

const antdSliderTrackStyle = {
  height: '8px',
  'background-image':
    'linear-gradient(to right top, rgb(185, 37, 255), rgb(162, 56, 255), rgb(137, 69, 255), rgb(110, 77, 255), rgb(77, 84, 255))',
  'border-radius': '80px',
}

interface Props extends Omit<FileInputProps, 'onChange' | 'onRemove'>, Pick<CropperProps, 'cropSize' | 'cropShape'> {
  onError: (error: string) => void
  onChange: (url: string) => void
}

const FileInputButton = (props: AriaButtonProps) => {
  const ref = useObjectRef<HTMLSpanElement>()
  const { buttonProps } = useButton({ elementType: 'span', ...props }, ref)
  const { hoverProps, isHovered } = useHover(props)
  const { isFocusVisible, focusProps } = useFocusRing()

  return (
    <span
      ref={ref}
      {...buttonProps}
      {...hoverProps}
      {...focusProps}
      className={cx(Styles.fileUploadButton, { [Styles.hovered]: isHovered, [Styles.focusVisible]: isFocusVisible })}
    >
      <CameraIcon />
    </span>
  )
}

const FileRemoveButton = (props: AriaButtonProps) => {
  const ref = useObjectRef<HTMLButtonElement>()
  const { buttonProps } = useButton(props, ref)
  const { hoverProps, isHovered } = useHover(props)
  const { isFocusVisible, focusProps } = useFocusRing()

  return (
    <button
      ref={ref}
      {...buttonProps}
      {...hoverProps}
      {...focusProps}
      className={cx(Styles.fileRemoveButton, { [Styles.hovered]: isHovered, [Styles.focusVisible]: isFocusVisible })}
    >
      <RemoveIcon />
    </button>
  )
}

interface FileInputProps {
  label: string
  children: ReactNode
  onChange: (e: React.ChangeEvent<HTMLInputElement>) => void
  value: string
  labelContainerClassName?: string
  onRemove: () => void
  useControls?: boolean
}

const FileInput = forwardRef(
  (
    { children, value, onChange, onRemove, labelContainerClassName, useControls = true, ...props }: FileInputProps,
    ref: any,
  ) => {
    const inputRef = useObjectRef<HTMLInputElement>(ref)
    const { labelProps, fieldProps } = useInputField(props)

    const handleFileInputPress = (e: PressEvent) => {
      if (e.pointerType === 'keyboard') {
        inputRef.current?.click()
      }
    }

    const Label = () => {
      // if not using controls render the children as the label (mainly for bc)
      if (!useControls) {
        return <label {...labelProps}>{children}</label>
      }

      return (
        <>
          {children}
          <div className={cx(Styles.labelContainer, { [Styles.emptyImage]: !value }, labelContainerClassName)}>
            <label {...labelProps} className={Styles.label}>
              <FileInputButton onPress={handleFileInputPress} />
            </label>
            {value && value.length > 0 && (
              <div className={Styles.removeButtonContainer}>
                <FileRemoveButton onPress={onRemove} />
              </div>
            )}
          </div>
        </>
      )
    }

    return (
      <div className={Styles.container}>
        <Label />
        <input
          ref={inputRef}
          {...fieldProps}
          className={Styles.hide}
          type="file"
          onChange={onChange}
          accept={'.png, .jpg, .jpeg'}
        />
      </div>
    )
  },
)

export const ImageUploadFieldModal = ({
  children,
  value,
  onChange,
  onError,
  labelContainerClassName,
  cropShape = 'rect',
  cropSize = { width: CROP_DIAMETER, height: CROP_DIAMETER },
  useControls = true,
  ...props
}: Props) => {
  const [crop, setCrop] = useState({ x: 1, y: 1 })
  const [localPhotoFilePath, setLocalPhotoFilePath] = useState<string | null>(value)
  const [isCropping, setIsCropping] = useState(false)
  const [isZoomSet, setIsZoomSet] = useState(false)
  const [minZoom, setMinZoom] = useState(0)
  const [maxZoom, setMaxZoom] = useState(0)
  const inputRef = useObjectRef<HTMLInputElement>()
  const [croppedAreaPixels, setCroppedAreaPixels] = useState<{
    width: number
    height: number
    x: number
    y: number
  } | null>(null)
  const [zoom, setZoom] = useState(1)

  useEffect(() => {
    setMaxZoom(minZoom * MAX_ZOOM_MULTIPLIER)
  }, [minZoom])

  const uploadFile = (file: Blob) =>
    externalService.uploadPhoto(file, cropSize.width, cropSize.height).json(({ secure_url }) => onChange(secure_url))

  const handleFileInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { files } = e.target
    const file = files?.item(0)

    if (file) {
      openCropWindow(file)
    } else {
      setLocalPhotoFilePath(null)
    }
  }

  const openCropWindow = (file: Blob) => {
    let reader = new FileReader()

    reader.onload = () => {
      const newFilePath = reader.result

      if (typeof newFilePath === 'string') {
        setLocalPhotoFilePath(newFilePath)
        setIsCropping(true)
      } else if (newFilePath) {
        const stringPath = Buffer.from(newFilePath).toString('utf-8')
        setLocalPhotoFilePath(stringPath)
        setIsCropping(true)
      }
    }

    reader.onerror = () => {
      showToastMessage('There was a problem uploading your profile image. Please try again later.', 'error')
    }

    readImage(reader, file)
  }

  const onCropChange = (crop: { x: number; y: number }) => {
    if (!isZoomSet) {
      const minZoom = getCropperZoom(cropSize)

      setIsZoomSet(true)
      setMinZoom(minZoom)
    }

    setCrop(crop)
  }

  const onCropComplete = (
    croppedArea: any,
    croppedAreaPixels: { height: number; width: number; x: number; y: number },
  ) => {
    setCroppedAreaPixels(croppedAreaPixels)
  }

  const onZoomChange = (zoom: number) => {
    setZoom(zoom)
  }

  const onCropCancel = () => {
    setLocalPhotoFilePath(null)
    setIsCropping(false)
    setIsZoomSet(false)
    if (inputRef.current) inputRef.current.value = ''
  }

  const handleRemove = () => {
    if (inputRef.current) {
      inputRef.current.value = ''
      setLocalPhotoFilePath(null)
      onChange('')
    }
  }

  const onCropFinish = async () => {
    if (localPhotoFilePath && croppedAreaPixels) {
      try {
        const croppedImage = (await getCroppedImg(localPhotoFilePath, croppedAreaPixels, {
          imageQuality: 0.92,
        })) as Blob
        setLocalPhotoFilePath(URL.createObjectURL(croppedImage))
        if (inputRef.current) {
          inputRef.current.value = ''
        }
        uploadFile(croppedImage)
      } catch (e) {
        onError("We couldn't process your photo crop, please try again")
      }
    }

    setIsCropping(false)
    setIsZoomSet(false)
  }

  const CropperModalTitle = () => {
    return <div className={Styles.cropperModalTitle}>Adjust zoom & position</div>
  }

  return (
    <>
      {isCropping && localPhotoFilePath && (
        <Modal
          visible={!!isCropping && !!localPhotoFilePath}
          onCancel={() => {
            setIsCropping(false)
            setLocalPhotoFilePath(null)
          }}
          wrapClassName={Styles.cropperModal}
          footer={null}
          width={448}
          title={<CropperModalTitle />}
          closeIcon={<CloseIcon className={Styles.modalCloseIcon} />}
        >
          <div className={Styles.cropperWrapper}>
            <Cropper
              image={localPhotoFilePath}
              crop={crop}
              zoom={zoom}
              minZoom={minZoom}
              maxZoom={maxZoom}
              aspect={1}
              onCropChange={onCropChange}
              onCropComplete={onCropComplete}
              onZoomChange={onZoomChange}
              cropShape={cropShape}
              cropSize={cropSize}
              style={{
                containerStyle: {},
                mediaStyle: {},
                cropAreaStyle: {
                  color: '#2B2F38',
                  opacity: '0.75',
                },
              }}
              classes={{
                containerClassName: Styles.cropperContainer,
                mediaClassName: cropperImageClass,
                cropAreaClassName: '',
              }}
            />
            <div className={Styles.zoomInfoText}>Drag the image to adjust its position.</div>
          </div>
          <div className={Styles.cropControls}>
            <div className={Styles.zoomContainer}>
              <div className={Styles.zoomCueWrapper}>
                <span className={Styles.icon}>-</span>
                <span className={Styles.text}>Avatar Zoom</span>
                <span className={Styles.icon}>+</span>
              </div>
              <Slider
                className={Styles.zoomSlider}
                value={zoom}
                min={minZoom}
                max={maxZoom}
                step={(maxZoom - minZoom) / 100}
                tooltipVisible={false}
                onChange={(e) => onZoomChange(e as number)}
                // @ts-ignore
                trackStyle={antdSliderTrackStyle}
                handleStyle={antdSliderHandleStyles}
              />
            </div>
            <div className={Styles.buttonContainer}>
              <Button size="md" onPress={onCropCancel} className={Styles.cancelButton} variant="tertiary">
                Cancel
              </Button>
              <Button size="md" onPress={onCropFinish} className={Styles.saveButton}>
                Save
              </Button>
            </div>
          </div>
        </Modal>
      )}
      <FileInput
        {...props}
        onChange={handleFileInputChange}
        value={value}
        onRemove={handleRemove}
        labelContainerClassName={labelContainerClassName}
        ref={inputRef}
        useControls={useControls}
      >
        {children}
      </FileInput>
    </>
  )
}

interface FormikImageUploadFields extends Pick<CropperProps, 'cropSize' | 'cropShape'> {
  children: (url: string) => ReactNode
  label: string
  labelContainerClassName?: string
}

export const FormikImageUploadFieldModal = ({
  children,
  labelContainerClassName,
  onChange,
  ...props
}: FieldHookConfig<string> & FormikImageUploadFields) => {
  const [field, , { setValue, setError, setTouched }] = useField(props)

  const handleError = (e: string) => {
    setError(e)
    setTouched(true)
  }

  return (
    <ImageUploadFieldModal
      value={field.value}
      labelContainerClassName={labelContainerClassName}
      onChange={setValue}
      {...props}
      onError={handleError}
    >
      {children(field.value)}
    </ImageUploadFieldModal>
  )
}
